import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { HealthScore, KillSwitchState, MetricValue } from '@hospasense/contracts';
import {
  fetchHealthScores,
  fetchKillSwitch,
  fetchMetrics,
  fetchReports,
  type ReportListItem,
} from '../../lib/queries-s5.js';
import { fetchDriftCases, fetchInvoices } from '../../lib/queries-commercial.js';
import { Badge, PageHeader } from '../../components/ui/index.js';
import { HomeSection, QuickLinks, StatCard } from './home-shared.js';

function metricValue(metrics: MetricValue[], key: string): string {
  const m = metrics.find((x) => x.key === key);
  if (!m || m.value === null) return '—';
  if (m.unit === 'percent') return `${m.value}%`;
  if (m.unit === 'days') return `${m.value}d`;
  if (m.unit === 'ratio') return `${m.value}×`;
  return String(m.value);
}

function bandCounts(scores: HealthScore[]): Record<string, number> {
  return scores.reduce<Record<string, number>>((acc, s) => {
    const key = s.reviewBand ?? s.band ?? 'unknown';
    acc[key] = (acc[key] ?? 0) + 1;
    return acc;
  }, {});
}

/** Executive cockpit — the platform owner's morning screen. */
export function ExecutiveCockpit({ displayName }: { displayName: string }) {
  const [metrics, setMetrics] = useState<MetricValue[]>([]);
  const [scores, setScores] = useState<HealthScore[]>([]);
  const [reports, setReports] = useState<ReportListItem[]>([]);
  const [killSwitch, setKill] = useState<KillSwitchState | null>(null);

  useEffect(() => {
    void Promise.allSettled([
      fetchMetrics().then(setMetrics),
      fetchHealthScores().then(setScores),
      fetchReports().then(setReports),
      fetchKillSwitch().then(setKill),
    ]);
  }, []);

  const bands = bandCounts(scores);
  const incomplete = metrics.filter((m) => m.incomplete).length;

  return (
    <div className="space-y-6" data-testid="home-executive">
      <PageHeader
        title="Executive Cockpit"
        subtitle={`Good day, ${displayName}. Portfolio position at a glance — every figure drills through to its lineage.`}
      />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          label="Activation rate"
          value={metricValue(metrics, 'activation_rate')}
          hint="trailing 180 days"
          to="/analytics"
        />
        <StatCard
          label="Retention"
          value={metricValue(metrics, 'retention_rate')}
          hint="12-month cohort"
          to="/analytics"
        />
        <StatCard
          label="Invoice collection"
          value={metricValue(metrics, 'invoice_collection_rate')}
          hint="within grace window"
          to="/analytics"
        />
        <StatCard
          label="At-risk customers"
          value={bands.at_risk ?? 0}
          tone={(bands.at_risk ?? 0) > 0 ? 'error' : 'success'}
          hint={`${scores.length} scored`}
          to="/health"
        />
      </div>
      {incomplete > 0 && (
        <p className="text-xs text-warning">
          {incomplete} metric(s) computed from incomplete sources — flagged, never hidden.{' '}
          <Link to="/analytics" className="underline">
            Inspect lineage
          </Link>
        </p>
      )}
      <HomeSection title="Latest reports">
        {reports.length === 0 ? (
          <p className="text-sm text-text-muted">No report snapshots yet.</p>
        ) : (
          <ul className="space-y-1 text-sm">
            {reports.slice(0, 3).map((r) => (
              <li key={r.id} className="flex items-center gap-2">
                <Link to={`/reports/${r.id}`} className="font-medium text-link hover:underline">
                  {r.title}
                </Link>
                {r.hasIncompleteSources && <Badge tone="warning">incomplete sources</Badge>}
              </li>
            ))}
          </ul>
        )}
      </HomeSection>
      {killSwitch?.engaged && (
        <p className="text-sm font-medium text-error">
          Automation kill switch is ENGAGED: {killSwitch.reason ?? 'no reason recorded'}
        </p>
      )}
      <QuickLinks
        links={[
          { to: '/', label: 'Mission Overview' },
          { to: '/analytics', label: 'Analytics' },
          { to: '/reports', label: 'Reports' },
          { to: '/risk', label: 'Risk register' },
          { to: '/automation', label: 'Automation' },
        ]}
      />
    </div>
  );
}

/** Product operations home — pipeline, drift and incident pressure. */
export function ProductOpsHome({ displayName }: { displayName: string }) {
  const [scores, setScores] = useState<HealthScore[]>([]);
  const [driftCount, setDriftCount] = useState<number | null>(null);
  const [metrics, setMetrics] = useState<MetricValue[]>([]);

  useEffect(() => {
    void Promise.allSettled([
      fetchHealthScores().then(setScores),
      fetchDriftCases().then((d) =>
        setDriftCount(d.items.filter((x) => x.status !== 'resolved').length),
      ),
      fetchMetrics().then(setMetrics),
    ]);
  }, []);

  const bands = bandCounts(scores);
  return (
    <div className="space-y-6" data-testid="home-product-ops">
      <PageHeader
        title="Product Operations"
        subtitle={`Welcome back, ${displayName}. What needs operating today.`}
      />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          label="Open drift cases"
          value={driftCount ?? '—'}
          tone={(driftCount ?? 0) > 0 ? 'warning' : 'success'}
          to="/entitlements"
        />
        <StatCard
          label="Fleet on current release"
          value={metricValue(metrics, 'fleet_adoption_current_release')}
          to="/releases"
        />
        <StatCard
          label="SLA attainment"
          value={metricValue(metrics, 'sla_attainment')}
          hint="trailing 90 days"
          to="/support"
        />
        <StatCard
          label="Customers on watch"
          value={(bands.watch ?? 0) + (bands.at_risk ?? 0)}
          tone={(bands.at_risk ?? 0) > 0 ? 'warning' : 'success'}
          to="/health"
        />
      </div>
      <QuickLinks
        links={[
          { to: '/onboarding', label: 'Onboarding pipeline' },
          { to: '/provisioning', label: 'Provisioning' },
          { to: '/fleet', label: 'Fleet topology' },
          { to: '/automation', label: 'Automation' },
          { to: '/risk', label: 'Risk & incidents' },
        ]}
      />
    </div>
  );
}

/** Finance home — money in, money late. */
export function FinanceHome({ displayName }: { displayName: string }) {
  const [metrics, setMetrics] = useState<MetricValue[]>([]);
  const [overdue, setOverdue] = useState<number | null>(null);
  const [reports, setReports] = useState<ReportListItem[]>([]);

  useEffect(() => {
    void Promise.allSettled([
      fetchMetrics().then(setMetrics),
      fetchInvoices().then((r) =>
        setOverdue(r.items.filter((i) => i.status === 'overdue' || i.status === 'in_grace').length),
      ),
      fetchReports().then(setReports),
    ]);
  }, []);

  return (
    <div className="space-y-6" data-testid="home-finance">
      <PageHeader
        title="Finance"
        subtitle={`Hello ${displayName}. Collections and commercial position.`}
      />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <StatCard
          label="Invoice collection rate"
          value={metricValue(metrics, 'invoice_collection_rate')}
          hint="paid within grace, 180 days"
          to="/analytics"
        />
        <StatCard
          label="Overdue / in-grace invoices"
          value={overdue ?? '—'}
          tone={(overdue ?? 0) > 0 ? 'warning' : 'success'}
          to="/billing"
        />
        <StatCard
          label="Expansion signals"
          value={metricValue(metrics, 'expansion_signals')}
          hint="unresolved upsell-relevant drift"
          to="/analytics"
        />
      </div>
      <HomeSection title="Latest reports">
        <ul className="space-y-1 text-sm">
          {reports.slice(0, 3).map((r) => (
            <li key={r.id}>
              <Link to={`/reports/${r.id}`} className="font-medium text-link hover:underline">
                {r.title}
              </Link>
            </li>
          ))}
          {reports.length === 0 && <li className="text-text-muted">No report snapshots yet.</li>}
        </ul>
      </HomeSection>
      <QuickLinks
        links={[
          { to: '/billing', label: 'B2B invoicing' },
          { to: '/commercial', label: 'Contracts' },
          { to: '/reports', label: 'Reports' },
        ]}
      />
    </div>
  );
}
